"use client";

import {
  FormEvent,
  type ReactNode,
  useActionState,
  useEffect,
  useRef,
  useState,
} from "react";

import cn from "classnames";

import { Button } from "@/components/button/button.component";
import { Dropdown } from "@/components/dropdown/dropdown.component";
import { formatUsPhoneNumber } from "@/components/phoneInput/phoneInput.component";
import { RichText } from "@/components/richText/richText.component";

import { validateLeadCaptureFields } from "@/lib/leadCapture/validation";

import {
  type LeadCaptureState,
  submitLeadCapture,
} from "@/app/actions/leadCapture";
import { brown } from "@/app/font";
import { EVENTS } from "@/constants/analytics.constants";
import {
  FORM_EVENTS,
  toCustomerIntent,
} from "@/constants/formAnalytics.constants";
import { useFormSubmitAttemptTag } from "@/hooks/useFormSubmitAttemptTag";
import useTagging from "@/hooks/useTagging";

import type { FreeQuoteDrawerDefaults } from "./freeQuoteDrawer.defaults";
import { FreeQuoteDrawerChrome } from "./freeQuoteDrawerChrome.component";
import { FreeQuoteSuccess } from "./freeQuoteSuccess.component";

const inputClassName =
  "border-greyscale-strength focus:border-secondary min-h-14 w-full min-w-0 rounded-full border bg-white px-5 text-[18px] outline-none transition-colors focus:ring-1 focus:ring-secondary";

const darkInputClassName =
  "min-h-14 w-full min-w-0 appearance-none rounded-full border bg-white px-5 text-base text-greyscale-headline outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-primary lg:text-[18px]";

const INITIAL_STATE: LeadCaptureState = { success: false };

const fieldOrder: ContactField[] = ["name", "email", "phone", "zip"];

const SOCIAL_PLATFORM_OPTIONS = [
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "x", label: "X (Twitter)" },
  { id: "youtube", label: "YouTube" },
  { id: "other", label: "Other" },
] as const;

export const FreeQuoteForm = ({
  content,
  consentText,
  variant = "drawer",
  theme = "standard",
  formLayout = "horizontal",
  showInstallationPreference = true,
  showEmployeePromoCode = false,
  showSocialPlatformReferralSource = false,
  banner,
}: FreeQuoteFormProps) => {
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    zip: "",
    installationPreference: "",
    employeePromoCode: "",
    socialPlatformReferralSource: "",
  });
  const [errors, setErrors] = useState<FreeQuoteErrors>({});
  const [state, formAction, isPending] = useActionState(
    submitLeadCapture,
    INITIAL_STATE,
  );
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const fieldRefs = useRef<Record<ContactField, HTMLInputElement | null>>({
    name: null,
    email: null,
    phone: null,
    zip: null,
  });
  const [, setTag] = useTagging();
  const prevStateRef = useRef(state);
  const isInline = variant === "inline";
  const formEvent = isInline
    ? FORM_EVENTS.HORIZONTAL_QUOTE.formEvent
    : FORM_EVENTS.HEADER_FLYOUT.formEvent;
  const isDrawer = variant === "drawer";
  const isDark = isInline && theme === "darkBlue";
  const idPrefix = isInline ? "free-quote-inline" : "free-quote";

  useFormSubmitAttemptTag(isPending, formEvent);

  const setValue = (field: keyof typeof values, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  const validationOptions = {
    requireInstallationPreference: showInstallationPreference,
    requireEmployeePromoCode: showEmployeePromoCode,
    requireSocialPlatformReferralSource: showSocialPlatformReferralSource,
  };

  const focusFirstInvalid = (nextErrors: FreeQuoteErrors) => {
    const firstInvalidField = [
      ...fieldOrder,
      ...(showInstallationPreference
        ? (["installationPreference"] as const)
        : []),
      ...(showEmployeePromoCode ? (["employeePromoCode"] as const) : []),
      ...(showSocialPlatformReferralSource
        ? (["socialPlatformReferralSource"] as const)
        : []),
    ].find((field) => nextErrors[field]);
    if (!firstInvalidField) return;
    if (firstInvalidField === "installationPreference") {
      document.getElementById(`${idPrefix}-installation`)?.focus();
      return;
    }
    if (firstInvalidField === "employeePromoCode") {
      document.getElementById(`${idPrefix}-employee-promo`)?.focus();
      return;
    }
    if (firstInvalidField === "socialPlatformReferralSource") {
      document.getElementById(`${idPrefix}-social-platform`)?.focus();
      return;
    }
    fieldRefs.current[firstInvalidField]?.focus();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (isPending) {
      event.preventDefault();
      return;
    }
    const nextErrors = validateLeadCaptureFields(
      values,
      "freeQuote",
      validationOptions,
    );
    setErrors(nextErrors);

    const firstInvalidField = [
      ...fieldOrder,
      ...(showInstallationPreference
        ? (["installationPreference"] as const)
        : []),
      ...(showEmployeePromoCode ? (["employeePromoCode"] as const) : []),
      ...(showSocialPlatformReferralSource
        ? (["socialPlatformReferralSource"] as const)
        : []),
    ].find((field) => nextErrors[field]);
    if (firstInvalidField) {
      event.preventDefault();
      focusFirstInvalid(nextErrors);
      return;
    }

    setTag({ event: EVENTS.FORM_SUBMIT_PRE_API_CALL, formEvent });
  };

  useEffect(() => {
    if (prevStateRef.current === state) return;
    prevStateRef.current = state;

    if (state.errors) {
      setErrors(state.errors);
      focusFirstInvalid(state.errors);
      const firstInvalidField = fieldOrder.find(
        (field) => state.errors?.[field],
      );
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...formEvent,
          fieldName: firstInvalidField,
          errorCode: firstInvalidField
            ? state.errors[firstInvalidField]
            : undefined,
        },
      });
    }

    if (state.formError) {
      setTag({
        event: EVENTS.FORM_SUBMIT_FAILURE,
        formEvent: {
          ...formEvent,
          errorCode: state.formError,
        },
      });
    }

    if (state.success) {
      const leadId = state.data?.leadId;
      const customerIntent = toCustomerIntent(state.data?.installPreference);
      setTag({
        event: EVENTS.FORM_SUBMIT_COMPLETE,
        outcome: state.data?.leadOutcome ?? "success",
        ...(leadId ? { activeProspectLeadID: leadId } : {}),
        ...(customerIntent ? { customer_intent: customerIntent } : {}),
        formEvent,
      });
    }
  }, [state, setTag, formEvent]);

  useEffect(() => {
    if (state.success) successHeadingRef.current?.focus();
  }, [state.success]);

  if (state.success) {
    if (isInline && !isDark) {
      return (
        <div role="status" aria-live="polite" className="py-8">
          <h3
            ref={successHeadingRef}
            tabIndex={-1}
            className="text-greyscale-headline text-3xl font-bold outline-none">
            {content.successHeading}
          </h3>
          <p className="text-greyscale-body mt-4 text-lg">
            {content.successMessage}
          </p>
        </div>
      );
    }

    const firstName = state.data?.name?.trim().split(/\s+/)[0];
    const installPref = state.data?.installPreference;
    return (
      <FreeQuoteSuccess
        content={content}
        headingRef={successHeadingRef}
        theme={theme}
        firstName={firstName}
        installPref={installPref}
      />
    );
  }

  const hasErrors = Boolean(Object.keys(errors).length || state.formError);
  const errorSummary = darkFieldSummary(errors, content, state.formError);
  const isFormFilled =
    Object.keys(
      validateLeadCaptureFields(values, "freeQuote", validationOptions),
    ).length === 0;
  const isThreeColumn = isInline && formLayout === "threeColumn";
  const hasLandingExtras =
    showEmployeePromoCode || showSocialPlatformReferralSource;
  // Landing extras wrap to a second lg row (4-col). Pin the CTA to the last
  // column when that row would otherwise leave a trailing gap.
  const secondRowFieldCount =
    Number(showInstallationPreference) +
    Number(showEmployeePromoCode) +
    Number(showSocialPlatformReferralSource);
  const pinSubmitToEnd =
    !isThreeColumn && hasLandingExtras && secondRowFieldCount < 3;
  const inlineGridCols = isThreeColumn
    ? "sm:grid-cols-3 lg:mx-auto lg:max-w-5xl"
    : hasLandingExtras
      ? "lg:grid-cols-4"
      : showInstallationPreference
        ? "lg:grid-cols-[repeat(4,minmax(0,1fr))_minmax(13rem,1.2fr)_auto]"
        : "lg:grid-cols-[repeat(4,minmax(0,1fr))_auto]";
  const labelClassName = isDark
    ? "text-[18px] font-semibold text-white"
    : "text-greyscale-headline text-[16px] leading-tight font-semibold";
  const errorClassName = isDark ? "text-error-on-dark" : "text-error";

  const form = (
    <form
      action={formAction}
      onSubmit={handleSubmit}
      noValidate
      data-testid={isInline ? "free-quote-inline-form" : "free-quote-form"}
      className={
        isInline
          ? cn(
              "grid w-full min-w-0 grid-cols-1 overflow-visible lg:items-start lg:gap-5",
              isDark ? "gap-4" : "gap-5",
              inlineGridCols,
            )
          : "flex flex-col gap-6"
      }>
      {isDark ? (
        <div className="lg-order-none mb-3 text-left text-xs leading-[1.45] max-[1023px]:order-last lg:col-span-full lg:mb-0 [&_a]:font-semibold [&_a]:text-white [&_a]:underline [&_a]:underline-offset-2 [&_p]:text-left">
          <RichText children={consentText ?? content.consentText} />
        </div>
      ) : null}

      {showEmployeePromoCode && showSocialPlatformReferralSource ? (
        <div className="hidden">
          <input type="hidden" name="mode" value="employeeRef" />
          <input type="hidden" name="source" value="Employee Referral" />
        </div>
      ) : (
        <div className="hidden">
          <input type="hidden" name="mode" value="freeQuote" />
          <input type="hidden" name="source" value="Free quote" />
        </div>
      )}

      {showInstallationPreference ? (
        <input
          type="hidden"
          name="collectInstallationPreference"
          value="true"
        />
      ) : null}
      {showEmployeePromoCode ? (
        <input type="hidden" name="collectEmployeePromoCode" value="true" />
      ) : null}
      {showSocialPlatformReferralSource ? (
        <input
          type="hidden"
          name="collectSocialPlatformReferralSource"
          value="true"
        />
      ) : null}

      {isDark && !hasErrors ? (
        <p className="legal lg:col-span-full">
          {content.requiredFieldsMessage}
        </p>
      ) : null}

      {hasErrors ? (
        <p
          role="alert"
          className={cn(
            "text-sm font-semibold lg:col-span-full",
            errorClassName,
          )}>
          {isDark
            ? errorSummary
            : (state.formError ?? "Please correct the highlighted fields.")}
        </p>
      ) : null}

      <FormField
        label={content.nameLabel}
        htmlFor={`${idPrefix}-name`}
        error={errors.name}
        reserveErrorSpace={isInline}
        markError={isDark}
        variant={isDrawer}
        labelClassName={labelClassName}
        errorClassName={errorClassName}>
        <input
          ref={(node) => {
            fieldRefs.current.name = node;
          }}
          id={`${idPrefix}-name`}
          name="name"
          type="text"
          autoComplete="name"
          placeholder={content.namePlaceholder}
          value={values.name}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? `${idPrefix}-name-error` : undefined}
          onChange={(event) => setValue("name", event.currentTarget.value)}
          className={contactInputClassName(isDark, errors.name)}
        />
      </FormField>

      <FormField
        label={content.emailLabel}
        htmlFor={`${idPrefix}-email`}
        error={errors.email}
        reserveErrorSpace={isInline}
        markError={isDark}
        variant={isDrawer}
        labelClassName={labelClassName}
        errorClassName={errorClassName}>
        <input
          ref={(node) => {
            fieldRefs.current.email = node;
          }}
          id={`${idPrefix}-email`}
          name="email"
          type="email"
          autoComplete="email"
          placeholder={content.emailPlaceholder}
          value={values.email}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={
            errors.email ? `${idPrefix}-email-error` : undefined
          }
          onChange={(event) => setValue("email", event.currentTarget.value)}
          className={contactInputClassName(isDark, errors.email)}
        />
      </FormField>

      <div
        className={
          isInline
            ? "grid grid-cols-2 gap-4 lg:contents"
            : "grid grid-cols-1 gap-6 min-[430px]:grid-cols-2"
        }>
        <FormField
          label={content.phoneFieldLabel}
          htmlFor={`${idPrefix}-phone`}
          error={errors.phone}
          reserveErrorSpace={isInline}
          markError={isDark}
          variant={isDrawer}
          labelClassName={labelClassName}
          errorClassName={errorClassName}>
          <input
            ref={(node) => {
              fieldRefs.current.phone = node;
            }}
            id={`${idPrefix}-phone`}
            name="phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={14}
            placeholder={content.phonePlaceholder}
            value={values.phone}
            aria-invalid={Boolean(errors.phone)}
            aria-describedby={
              errors.phone ? `${idPrefix}-phone-error` : undefined
            }
            onChange={(event) =>
              setValue("phone", formatUsPhoneNumber(event.currentTarget.value))
            }
            className={contactInputClassName(isDark, errors.phone)}
          />
        </FormField>

        <FormField
          label={content.zipLabel}
          htmlFor={`${idPrefix}-zip`}
          error={errors.zip}
          reserveErrorSpace={isInline}
          markError={isDark}
          variant={isDrawer}
          labelClassName={labelClassName}
          errorClassName={errorClassName}>
          <input
            ref={(node) => {
              fieldRefs.current.zip = node;
            }}
            id={`${idPrefix}-zip`}
            name="zip"
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={5}
            placeholder={content.zipPlaceholder}
            value={values.zip}
            aria-invalid={Boolean(errors.zip)}
            aria-describedby={errors.zip ? `${idPrefix}-zip-error` : undefined}
            onChange={(event) =>
              setValue("zip", event.currentTarget.value.replace(/\D/g, ""))
            }
            className={contactInputClassName(isDark, errors.zip)}
          />
        </FormField>
      </div>

      {showInstallationPreference ? (
        <>
          {isDark ? (
            <div className="flex flex-col gap-3 lg:hidden">
              <p
                id={`${idPrefix}-installation-label-mobile`}
                className={labelClassName}>
                Installation preference
                <span className="text-error-on-dark" aria-hidden="true">
                  *
                </span>
              </p>
              <div
                role="radiogroup"
                aria-labelledby={`${idPrefix}-installation-label-mobile`}
                aria-invalid={
                  Boolean(errors.installationPreference) || undefined
                }
                aria-describedby={
                  errors.installationPreference
                    ? `${idPrefix}-installation-mobile-error`
                    : undefined
                }
                className="flex flex-col gap-3">
                {content.installationPreferences.map((option) => {
                  const selected = values.installationPreference === option.id;
                  const onCard = Boolean(option.description);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() =>
                        setValue("installationPreference", option.id)
                      }
                      className={cn(
                        "focus-visible:outline-primary flex items-start gap-3 px-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid",
                        onCard ? "rounded-2xl bg-white py-4" : "py-1",
                      )}>
                      <span
                        className={cn(
                          "mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border-2",
                          onCard
                            ? cn(
                                "bg-white",
                                selected
                                  ? "border-secondary"
                                  : "border-[#c5c5c5]",
                              )
                            : "border-white",
                        )}
                        aria-hidden>
                        {selected ? (
                          <span
                            className={cn(
                              "size-2 rounded-full",
                              onCard ? "bg-secondary" : "bg-white",
                            )}
                          />
                        ) : null}
                      </span>
                      <span>
                        <span
                          className={cn(
                            "block font-semibold",
                            onCard ? "text-secondary" : "text-white",
                          )}>
                          {option.mobileLabel}
                        </span>
                        {option.description ? (
                          <span className="text-greyscale-body mt-1 block text-sm leading-snug">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.installationPreference ? (
                <p
                  id={`${idPrefix}-installation-mobile-error`}
                  className={cn("px-3 text-sm", errorClassName)}>
                  {errors.installationPreference}
                </p>
              ) : null}
            </div>
          ) : isDrawer ? (
            <div className="flex flex-col gap-3">
              <input
                type="hidden"
                name="installationPreference"
                value={values.installationPreference}
              />
              <p
                id={`${idPrefix}-installation-label-drawer`}
                className={cn(
                  labelClassName,
                  errors.installationPreference ? "text-error!" : null,
                )}>
                Installation preference
                <span className="text-error" aria-hidden="true">
                  *
                </span>
              </p>
              {errors.installationPreference ? (
                <p
                  id={`${idPrefix}-installation-drawer-error`}
                  className={cn("px-3 text-sm", errorClassName)}>
                  {errors.installationPreference}
                </p>
              ) : null}

              <div
                role="radiogroup"
                aria-labelledby={`${idPrefix}-installation-label-drawer`}
                aria-invalid={
                  Boolean(errors.installationPreference) || undefined
                }
                aria-describedby={
                  errors.installationPreference
                    ? `${idPrefix}-installation-drawer-error`
                    : undefined
                }
                className="flex flex-row flex-wrap space-y-3 min-[479px]:grid-cols-2 [&>*:first-child]:mr-3">
                {content.installationPreferences.map((option) => {
                  const selected = values.installationPreference === option.id;
                  const onCard = Boolean(option.description);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() =>
                        setValue("installationPreference", option.id)
                      }
                      className={cn(
                        "flex flex-row items-center gap-2 px-4 text-left",
                        onCard
                          ? "min-h-[134px] w-[calc(50%-.38rem)] rounded-2xl border border-[#808080] bg-white py-4"
                          : "w-full py-1",
                        selected && onCard ? "border-adt-blue border-2" : "",
                      )}>
                      <span
                        className={cn(
                          "mt-0.5 flex h-[24px] w-[24px] shrink-0 grow-0 items-center justify-center rounded-full border",
                          selected ? "border-secondary" : "border-[#808080]",
                        )}
                        aria-hidden>
                        {selected ? (
                          <span className="bg-secondary h-[12px] w-[12px] rounded-full" />
                        ) : null}
                      </span>
                      <span>
                        <span
                          className={cn(
                            brown.className,
                            "block text-[16px] font-semibold",
                            option.description
                              ? "mb-2 leading-[18px]"
                              : "leading-[24px]",
                          )}>
                          {option.mobileLabel}
                        </span>
                        {option.description ? (
                          <span className="text-greyscale-body pr-1 text-[14px] leading-[18px]">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {!isDrawer ? (
            <div className={isDark ? "hidden min-w-0 lg:block" : "min-w-0"}>
              <FormField
                label="Installation preference"
                htmlFor={`${idPrefix}-installation`}
                error={errors.installationPreference}
                reserveErrorSpace={isInline}
                markError={isDark}
                labelClassName={labelClassName}
                errorClassName={errorClassName}>
                <Dropdown
                  id={`${idPrefix}-installation`}
                  name="installationPreference"
                  value={values.installationPreference}
                  placeholder="Select option"
                  required
                  invalid={Boolean(errors.installationPreference)}
                  labelledBy={`${idPrefix}-installation-label`}
                  describedBy={
                    errors.installationPreference
                      ? `${idPrefix}-installation-error`
                      : undefined
                  }
                  options={content.installationPreferences.map((option) => ({
                    value: option.id,
                    label: option.label,
                  }))}
                  onChange={(nextValue) =>
                    setValue("installationPreference", nextValue)
                  }
                />
              </FormField>
            </div>
          ) : null}
        </>
      ) : null}

      {showEmployeePromoCode ? (
        <FormField
          label="Employee Promo Code"
          htmlFor={`${idPrefix}-employee-promo`}
          error={errors.employeePromoCode}
          reserveErrorSpace={isInline}
          markError={isDark}
          variant={isDrawer}
          labelClassName={labelClassName}
          errorClassName={errorClassName}>
          <input
            id={`${idPrefix}-employee-promo`}
            name="employeePromoCode"
            type="text"
            autoComplete="off"
            placeholder="Enter Promo Code"
            value={values.employeePromoCode}
            aria-invalid={Boolean(errors.employeePromoCode)}
            aria-describedby={
              errors.employeePromoCode
                ? `${idPrefix}-employee-promo-error`
                : undefined
            }
            onChange={(event) =>
              setValue("employeePromoCode", event.currentTarget.value)
            }
            className={contactInputClassName(isDark, errors.employeePromoCode)}
          />
        </FormField>
      ) : null}

      {showSocialPlatformReferralSource ? (
        <FormField
          label="Social Platform Referral Source"
          htmlFor={`${idPrefix}-social-platform`}
          error={errors.socialPlatformReferralSource}
          reserveErrorSpace={isInline}
          markError={isDark}
          variant={isDrawer}
          labelClassName={labelClassName}
          errorClassName={errorClassName}>
          <Dropdown
            id={`${idPrefix}-social-platform`}
            name="socialPlatformReferralSource"
            value={values.socialPlatformReferralSource}
            placeholder="Select platform"
            required
            invalid={Boolean(errors.socialPlatformReferralSource)}
            labelledBy={`${idPrefix}-social-platform-label`}
            describedBy={
              errors.socialPlatformReferralSource
                ? `${idPrefix}-social-platform-error`
                : undefined
            }
            options={SOCIAL_PLATFORM_OPTIONS.map((option) => ({
              value: option.id,
              label: option.label,
            }))}
            onChange={(nextValue) =>
              setValue("socialPlatformReferralSource", nextValue)
            }
          />
        </FormField>
      ) : null}

      {isInline ? null : (
        <div className="text-greyscale-body text-sm leading-[1.35]">
          <RichText children={content.consentText} />
        </div>
      )}

      <Button
        secondary={!isDark}
        primary={isDark}
        type="submit"
        loading={isPending}
        disabled={isPending}
        className={cn(
          "min-h-14 w-full px-6 py-3 max-[1023px]:order-last",
          isDark
            ? cn(
                "focus-visible:ring-primary! focus-visible:ring-offset-secondary normal-case! lg:w-fit lg:min-w-[180px]",
                !isThreeColumn && (hasLandingExtras ? "lg:mt-12" : "lg:mt-8"),
                isFormFilled ? "text-white!" : "text-secondary!",
              )
            : "bg-[#00226A]! hover:bg-[#07408B]! focus-visible:ring-[#00226A]",
          isInline &&
            !isDark &&
            !isThreeColumn &&
            cn(
              "lg:w-fit lg:min-w-[180px]",
              hasLandingExtras ? "lg:mt-12" : "lg:mt-8",
            ),
          isThreeColumn &&
            "sm:col-span-3 sm:mt-2 sm:w-fit sm:min-w-[180px] sm:justify-self-center",
          isInline && pinSubmitToEnd && "lg:col-start-4",
        )}>
        {isDark ? darkSubmitLabel(content.submitLabel) : content.submitLabel}
      </Button>
    </form>
  );

  if (isInline) {
    return (
      <>
        {banner ? <div className="mb-8">{banner}</div> : null}
        {form}
      </>
    );
  }

  return (
    <FreeQuoteDrawerChrome content={content}>{form}</FreeQuoteDrawerChrome>
  );
};

const darkFieldSummary = (
  errors: FreeQuoteErrors,
  content: FreeQuoteDrawerDefaults,
  formError?: string,
) => {
  if (formError) return formError;
  const labels = [
    errors.name && content.nameLabel,
    errors.email && content.emailLabel,
    errors.phone && content.phoneFieldLabel,
    errors.zip && content.zipLabel,
    errors.installationPreference && "Installation preference",
    errors.employeePromoCode && "Employee Promo Code",
    errors.socialPlatformReferralSource && "Social Platform Referral Source",
  ].filter((label): label is string => Boolean(label));
  if (labels.length === 0) return "Please correct the highlighted fields.";
  if (labels.length === 1) return `Check ${labels[0]} below.`;
  const last = labels[labels.length - 1];
  return `Check ${labels.slice(0, -1).join(", ")} and ${last} below.`;
};

const ErrorMark = () => (
  <span
    className="bg-error-on-dark pointer-events-none absolute top-1/2 right-4 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-xs font-bold text-white lg:hidden"
    aria-hidden>
    !
  </span>
);

const FormField = ({
  label,
  htmlFor,
  error,
  reserveErrorSpace,
  labelClassName,
  errorClassName,
  markError,
  children,
  variant,
}: FormFieldProps) => (
  <div className="flex min-w-0 flex-col gap-2">
    <label
      id={`${htmlFor}-label`}
      htmlFor={htmlFor}
      className={cn(labelClassName, variant && error ? "text-error!" : null)}>
      {label}
      <span
        className={variant ? "text-error" : "text-error-on-dark"}
        aria-hidden="true">
        *
      </span>
    </label>
    {markError ? (
      <div className="relative">
        {children}
        {error ? <ErrorMark /> : null}
      </div>
    ) : (
      children
    )}
    {error ? (
      <p
        id={`${htmlFor}-error`}
        className={cn("min-h-10 px-3 text-sm", errorClassName)}>
        {error}
      </p>
    ) : reserveErrorSpace ? (
      <p
        className={cn("min-h-10 px-3 text-sm", markError && "max-lg:hidden")}
        aria-hidden>
        {"\u00a0"}
      </p>
    ) : null}
  </div>
);

const darkSubmitLabel = (label: string) =>
  label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();

const contactInputClassName = (isDark: boolean, error?: string) =>
  isDark
    ? cn(
        darkInputClassName,
        error ? "border-error-on-dark pr-12 lg:pr-5" : "border-transparent",
      )
    : cn(inputClassName, error && "border-error focus:border-error");

type ContactField = "name" | "email" | "phone" | "zip";
type FreeQuoteErrors = Partial<
  Record<
    | ContactField
    | "installationPreference"
    | "employeePromoCode"
    | "socialPlatformReferralSource",
    string
  >
>;

type FormFieldProps = {
  label: string;
  htmlFor: string;
  error?: string;
  reserveErrorSpace?: boolean;
  labelClassName: string;
  errorClassName: string;
  markError?: boolean;
  children: ReactNode;
  variant?: boolean;
};

type FreeQuoteFormProps = {
  content: FreeQuoteDrawerDefaults;
  consentText?: string;
  variant?: "drawer" | "inline";
  theme?: "standard" | "darkBlue";
  formLayout?: "horizontal" | "threeColumn";
  showInstallationPreference?: boolean;
  showEmployeePromoCode?: boolean;
  showSocialPlatformReferralSource?: boolean;
  banner?: ReactNode;
};
